import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "submitButton", "submitButtonSpinner", "submitButtonText"]

  connect() {
    this.formTarget.addEventListener("submit", this.validateForm.bind(this))
  }

  validateForm(event) {
    const errors = this.collectValidationErrors()

    if (errors.length > 0) {
      event.preventDefault()
      this.showValidationErrors(errors)
      return false
    }

    // Show loading state when validation passes
    this.setSubmitButtonLoading(true)

    return true
  }

  collectValidationErrors() {
    const errors = []

    // Check if at least one territory is included or excluded
    const includedTerritories = document.getElementById("album_included_territories")
    const excludedTerritories = document.getElementById("album_excluded_territories")

    let hasIncludedTerritories = false
    let hasExcludedTerritories = false

    if (includedTerritories && includedTerritories.value) {
      try {
        const included = JSON.parse(includedTerritories.value)
        hasIncludedTerritories = Array.isArray(included) && included.length > 0
      } catch (e) {
        hasIncludedTerritories = false
      }
    }

    if (excludedTerritories && excludedTerritories.value) {
      try {
        const excluded = JSON.parse(excludedTerritories.value)
        hasExcludedTerritories = Array.isArray(excluded) && excluded.length > 0
      } catch (e) {
        hasExcludedTerritories = false
      }
    }

    if (!hasIncludedTerritories && !hasExcludedTerritories) {
      errors.push("At least one territory must be included or excluded")
    }

    // Check if at least one DSP is selected
    const selectedDsps = document.getElementById("album_selected_dsps")
    if (selectedDsps && selectedDsps.value) {
      try {
        const dsps = JSON.parse(selectedDsps.value)
        if (!Array.isArray(dsps) || dsps.length === 0) {
          errors.push("At least one DSP must be selected")
        }
      } catch (e) {
        errors.push("At least one DSP must be selected")
      }
    } else {
      errors.push("At least one DSP must be selected")
    }

    return errors
  }

  showValidationErrors(errors) {
    // Remove any existing error display
    this.removeExistingErrors()

    // Create error summary
    const errorContainer = document.createElement('div')
    errorContainer.className = 'rounded-md bg-red-50 p-4 mb-6'
    errorContainer.id = 'client-validation-errors'

    const errorContent = `
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            There ${errors.length === 1 ? 'is' : 'are'} ${errors.length} error${errors.length === 1 ? '' : 's'} with your submission
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-disc pl-5 space-y-1">
              ${errors.map(error => `<li>${error}</li>`).join('')}
            </ul>
          </div>
        </div>
      </div>
    `

    errorContainer.innerHTML = errorContent

    // Insert error container at the top of the form
    this.formTarget.insertBefore(errorContainer, this.formTarget.firstChild)

    // Scroll to the top of the form to show errors
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Highlight sections with red borders
    this.highlightErrorSections()
  }

  removeExistingErrors() {
    const existingErrors = document.getElementById('client-validation-errors')
    if (existingErrors) {
      existingErrors.remove()
    }

    // Remove red border from territory sections
    const includedSection = document.querySelector('[data-territory-section="included"]')
    if (includedSection) {
      includedSection.classList.remove('border-red-500')
      includedSection.classList.add('border-gray-300')
    }

    const excludedSection = document.querySelector('[data-territory-section="excluded"]')
    if (excludedSection) {
      excludedSection.classList.remove('border-red-500')
      excludedSection.classList.add('border-gray-300')
    }

    // Remove red border from DSP section
    const dspSection = document.querySelector('[data-dsp-section]')
    if (dspSection) {
      dspSection.classList.remove('border-red-500')
      dspSection.classList.add('border-gray-300')
    }
  }

  highlightErrorSections() {
    // Highlight territory sections if both are empty
    const includedTerritories = document.getElementById("album_included_territories")
    const excludedTerritories = document.getElementById("album_excluded_territories")

    let hasIncludedTerritories = false
    let hasExcludedTerritories = false

    if (includedTerritories && includedTerritories.value) {
      try {
        const included = JSON.parse(includedTerritories.value)
        hasIncludedTerritories = Array.isArray(included) && included.length > 0
      } catch (e) {
        hasIncludedTerritories = false
      }
    }

    if (excludedTerritories && excludedTerritories.value) {
      try {
        const excluded = JSON.parse(excludedTerritories.value)
        hasExcludedTerritories = Array.isArray(excluded) && excluded.length > 0
      } catch (e) {
        hasExcludedTerritories = false
      }
    }

    if (!hasIncludedTerritories && !hasExcludedTerritories) {
      const includedSection = document.querySelector('[data-territory-section="included"]')
      if (includedSection) {
        includedSection.classList.remove('border-gray-300')
        includedSection.classList.add('border-red-500')
      }

      const excludedSection = document.querySelector('[data-territory-section="excluded"]')
      if (excludedSection) {
        excludedSection.classList.remove('border-gray-300')
        excludedSection.classList.add('border-red-500')
      }
    }

    // Highlight DSP section if no DSPs selected
    const selectedDsps = document.getElementById("album_selected_dsps")
    const dspSection = document.querySelector('[data-dsp-section]')

    if (dspSection && selectedDsps) {
      try {
        const dsps = JSON.parse(selectedDsps.value)
        if (!Array.isArray(dsps) || dsps.length === 0) {
          dspSection.classList.remove('border-gray-300')
          dspSection.classList.add('border-red-500')
        }
      } catch (e) {
        dspSection.classList.remove('border-gray-300')
        dspSection.classList.add('border-red-500')
      }
    }
  }

  setSubmitButtonLoading(loading) {
    if (!this.hasSubmitButtonTarget) return

    if (loading) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
      this.submitButtonTarget.classList.remove('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSubmitButtonSpinnerTarget) {
        this.submitButtonSpinnerTarget.classList.remove('hidden')
      }

      if (this.hasSubmitButtonTextTarget) {
        this.submitButtonTextTarget.textContent = 'Saving...'
      }
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed')
      this.submitButtonTarget.classList.add('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSubmitButtonSpinnerTarget) {
        this.submitButtonSpinnerTarget.classList.add('hidden')
      }

      if (this.hasSubmitButtonTextTarget) {
        this.submitButtonTextTarget.textContent = 'SAVE AND CONTINUE →'
      }
    }
  }
}
